import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  SelectChangeEvent
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import { useTickets } from '@services/helpdesk/helpdesk.hooks'
import { format, parseISO } from 'date-fns'
import { pl } from 'date-fns/locale'
import { Ticket, TicketFilters } from '@services/helpdesk/helpdesk.service'

// Mapping of statuses to Polish names and colors
const statusMap: Record<
  string,
  {
    label: string
    color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
  }
> = {
  NEW: { label: 'Nowy', color: 'primary' },
  IN_PROGRESS: { label: 'W trakcie', color: 'info' },
  WAITING_FOR_PARTS: { label: 'Oczekiwanie na części', color: 'warning' },
  DONE: { label: 'Zakończony', color: 'success' },
  CANCELLED: { label: 'Anulowany', color: 'error' }
}

// Ordering options with Polish labels
const orderingOptions = [
  { value: '-created_at', label: 'Od najnowszych' },
  { value: 'created_at', label: 'Od najstarszych' },
  { value: '-updated_at', label: 'Ostatnio aktualizowane' },
  { value: '-priority', label: 'Priorytet (malejąco)' },
  { value: 'priority', label: 'Priorytet (rosnąco)' },
  { value: '-fault_date', label: 'Data usterki (najnowsze)' },
  { value: 'fault_date', label: 'Data usterki (najstarsze)' },
  { value: 'status', label: 'Status (rosnąco)' },
  { value: '-status', label: 'Status (malejąco)' }
]

const HelpdeskTickets = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState<TicketFilters>({
    ordering: '-created_at' // Default: newest first
  })
  const [progressFilter, setProgressFilter] = useState<'all' | 'unresolved' | 'resolved'>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  const { data: ticketsData, isLoading, error } = useTickets(filters)

  // Get tickets array from API response
  const tickets = ticketsData?.results || []

  // Filter tickets based on search term
  const filteredTickets = tickets.filter((ticket: Ticket) => {
    const searchLower = searchTerm.toLowerCase()
    return (
      ticket.topic.toLowerCase().includes(searchLower) ||
      ticket.description.toLowerCase().includes(searchLower) ||
      ticket.investmentName.toLowerCase().includes(searchLower) ||
      ticket.subjectName.toLowerCase().includes(searchLower) ||
      ticket.faultCategoryName.toLowerCase().includes(searchLower)
    )
  })

  const handleTicketClick = (id: number) => {
    navigate(`/helpdesk/tickets/${id}`)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const clearSearch = () => {
    setSearchTerm('')
  }

  const handleOrderingChange = (event: SelectChangeEvent<string>) => {
    setFilters((prev) => ({
      ...prev,
      ordering: event.target.value
    }))
  }

  const handleProgressFilterChange = (
    _event: React.MouseEvent<HTMLElement>,
    newValue: 'all' | 'unresolved' | 'resolved' | null
  ) => {
    if (newValue !== null) {
      setProgressFilter(newValue)
      if (newValue === 'all') {
        setFilters((prev) => {
          const { isInProgress, ...rest } = prev
          return rest
        })
      } else {
        setFilters((prev) => ({
          ...prev,
          isInProgress: newValue === 'unresolved'
        }))
      }
    }
  }

  const handleStatusChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value
    setStatusFilter(value)
    if (value === 'all') {
      setFilters((prev) => {
        const { status, ...rest } = prev
        return rest
      })
    } else {
      setFilters((prev) => ({
        ...prev,
        status: value
      }))
    }
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ mt: 2 }}>
        Wystąpił błąd podczas ładowania zgłoszeń: {(error as Error).message}
      </Alert>
    )
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant='h4' component='h1' gutterBottom>
        Zgłoszenia serwisowe
      </Typography>

      <Stack spacing={2} sx={{ mb: 3 }}>
        {/* Filters row */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={progressFilter}
            exclusive
            onChange={handleProgressFilterChange}
            aria-label='filter progress'
            size='small'
          >
            <ToggleButton value='all'>Wszystkie</ToggleButton>
            <ToggleButton value='unresolved'>W toku</ToggleButton>
            <ToggleButton value='resolved'>Rozwiązane</ToggleButton>
          </ToggleButtonGroup>

          <FormControl sx={{ minWidth: 180 }} size='small'>
            <InputLabel id='status-select-label'>Status</InputLabel>
            <Select
              labelId='status-select-label'
              id='status-select'
              value={statusFilter}
              label='Status'
              onChange={handleStatusChange}
            >
              <MenuItem value='all'>Wszystkie</MenuItem>
              <MenuItem value='NEW'>Nowy</MenuItem>
              <MenuItem value='IN_PROGRESS'>W trakcie</MenuItem>
              <MenuItem value='WAITING_FOR_PARTS'>Oczekiwanie na części</MenuItem>
              <MenuItem value='DONE'>Zakończony</MenuItem>
              <MenuItem value='CANCELLED'>Anulowany</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 220 }} size='small'>
            <InputLabel id='ordering-select-label'>Sortowanie</InputLabel>
            <Select
              labelId='ordering-select-label'
              id='ordering-select'
              value={filters.ordering || '-created_at'}
              label='Sortowanie'
              onChange={handleOrderingChange}
            >
              {orderingOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <TextField
          fullWidth
          variant='outlined'
          placeholder='Szukaj zgłoszeń...'
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position='end'>
                <IconButton onClick={clearSearch} edge='end'>
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Stack>

      {filteredTickets.length === 0 ? (
        <Alert severity='info'>Brak zgłoszeń spełniających kryteria wyszukiwania</Alert>
      ) : (
        <Grid container spacing={2}>
          {filteredTickets.map((ticket: Ticket) => (
            <Grid item xs={12} key={ticket.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    boxShadow: 6
                  }
                }}
                onClick={() => handleTicketClick(ticket.id)}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 1
                    }}
                  >
                    <Typography variant='h6' component='div'>
                      {ticket.topic}
                    </Typography>
                    <Chip
                      label={statusMap[ticket.status]?.label || ticket.status}
                      color={statusMap[ticket.status]?.color || 'default'}
                      size='small'
                    />
                  </Box>

                  <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                    {ticket.description.length > 150
                      ? `${ticket.description.substring(0, 150)}...`
                      : ticket.description}
                  </Typography>

                  <Box
                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Box>
                      <Typography variant='body2' component='span' sx={{ mr: 1 }}>
                        Inwestycja: {ticket.investmentName}
                      </Typography>
                      <Typography variant='body2' component='span'>
                        Lokal: {ticket.subjectName}
                      </Typography>
                      {ticket.customerAvailabilities &&
                        ticket.customerAvailabilities.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            <Typography
                              variant='body2'
                              component='div'
                              sx={{ fontWeight: 'medium' }}
                            >
                              Dostępność klienta:
                            </Typography>
                            {ticket.customerAvailabilities.map((availability) => (
                              <Typography
                                key={availability.id}
                                variant='body2'
                                component='div'
                                sx={{ ml: 1 }}
                              >
                                {format(parseISO(availability.availableFrom), 'dd.MM.yyyy HH:mm', {
                                  locale: pl
                                })}{' '}
                                -{' '}
                                {format(parseISO(availability.availableTo), 'HH:mm', {
                                  locale: pl
                                })}
                              </Typography>
                            ))}
                          </Box>
                        )}
                    </Box>

                    <Box>
                      <Typography variant='caption' color='text.secondary'>
                        Utworzono:{' '}
                        {format(new Date(ticket.createdAt), 'dd.MM.yyyy HH:mm', { locale: pl })}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  )
}

export default HelpdeskTickets
